'use client';

import { useContext, useEffect, useState } from 'react';
import { SparklesIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Brainstorm from './Brainstorm';
import { AuthContext } from '../AuthContext';

const MONGO_BASE = process.env.REACT_APP_MONGO_URL || 'https://analytics.insightos.co.uk';

export default function BrainstormPage() {
  const user = useContext(AuthContext);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [deletingBoardId, setDeletingBoardId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const loadBoards = async () => {
    setLoadingBoards(true);
    try {
      const response = await fetch(`${MONGO_BASE}/api/brainstorms`, {
        method: 'GET',
        credentials: 'include',
      });
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to load brainstorm boards');
      }

      const nextBoards = Array.isArray(payload.data) ? payload.data : [];
      setBoards(nextBoards);

      if (!selectedBoardId && nextBoards.length > 0) {
        setSelectedBoardId(nextBoards[0].brainstormId);
      }
    } catch (error) {
      console.error('Failed to load brainstorm boards', error);
      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, []);

  const createBoard = async () => {
    const trimmedTitle = newTitle.trim();
    const trimmedDescription = newDescription.trim();

    if (!trimmedTitle) {
      return;
    }

    setCreatingBoard(true);
    try {
      const response = await fetch(`${MONGO_BASE}/api/brainstorm`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: trimmedTitle,
          description: trimmedDescription,
          projectId: '',
          elements: [],
          createdBy: {
            id: user?.id || null,
            name: user?.name || 'Unknown',
          },
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to create brainstorm board');
      }

      const createdId = payload?.data?.brainstormId;
      await loadBoards();
      if (createdId) {
        setSelectedBoardId(createdId);
      }
      setNewTitle('');
      setNewDescription('');
    } catch (error) {
      console.error('Failed to create brainstorm board', error);
    } finally {
      setCreatingBoard(false);
    }
  };

  const deleteBoard = async (brainstormId) => {
    setDeletingBoardId(brainstormId);
    try {
      const response = await fetch(`${MONGO_BASE}/api/brainstorm/${encodeURIComponent(brainstormId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to delete brainstorm board');
      }

      const remainingBoards = boards.filter((board) => board.brainstormId !== brainstormId);
      setBoards(remainingBoards);

      if (selectedBoardId === brainstormId) {
        setSelectedBoardId(remainingBoards[0]?.brainstormId || '');
      }
    } catch (error) {
      console.error('Failed to delete brainstorm board', error);
    } finally {
      setDeletingBoardId('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-4">
        <div className="rounded-2xl border border-indigo-100 bg-white p-4 md:p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-6 w-6 text-indigo-600" />
              <h1 className="text-xl font-semibold text-slate-900">Brainstorm Boards</h1>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Board title"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500"
            />
            <button
              type="button"
              onClick={createBoard}
              disabled={creatingBoard || !newTitle.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <PlusIcon className="h-4 w-4" />
              {creatingBoard ? 'Creating...' : 'New Board'}
            </button>

            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Board description"
              rows={2}
              className="md:col-span-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {loadingBoards && <span className="text-sm text-slate-500">Loading boards...</span>}
            {!loadingBoards && boards.length === 0 && (
              <span className="text-sm text-slate-500">No boards yet. Create your first brainstorm board.</span>
            )}
            {boards.map((board) => {
              const selected = selectedBoardId === board.brainstormId;
              return (
                <div
                  key={board.brainstormId}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${
                    selected
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedBoardId(board.brainstormId)}
                      className="text-left"
                    >
                      <div className="font-semibold">{board.title || 'Untitled Brainstorm'}</div>
                      {board.description && (
                        <div className="mt-1 max-w-[250px] text-xs opacity-75 line-clamp-2">{board.description}</div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBoard(board.brainstormId)}
                      disabled={deletingBoardId === board.brainstormId}
                      title="Delete board"
                      className="ml-auto rounded-md p-1 text-red-500 hover:bg-red-50 disabled:opacity-60"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Brainstorm brainstormId={selectedBoardId} projectId={null} projectName="Standalone Brainstorm" />
      </div>
    </div>
  );
}
